import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { addTopic, getTopic, updateTopic } from '../api/topics';
import { RoomMembersContext } from '../App';
import { UserContext } from '../functions/Auth';
import ConfirmDialog from '../components/ConfirmDialog';

// component
import Lexical from './Lexical';
import FullScreenModal from './FullScreenModal';

// mui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// types
type propsType = {
    fnGetRoomTopics: (roomId: string, userId: number) => void,
    isEdit: boolean,
    isOpen: boolean,
    setIsEdit: React.Dispatch<React.SetStateAction<boolean>>,
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    targetEditTopic: number,
    topicType: "draft" | "published" | "new",
}

type ValidationErrorsType = {
    key: string,
    message: string,
} []

export default function TopicModal(props: propsType) {
    const {
        fnGetRoomTopics,
        isEdit,
        isOpen,
        setIsEdit,
        setIsOpen,
        targetEditTopic,
        topicType,
    } = props;
    const params = useParams();
    const roomId = params.roomId as string;
    const userInfo = useContext(UserContext);
    const userId = userInfo?.userId as number;

    // ルームメンバーの取得
    const roomMembers = useContext(RoomMembersContext);
    const roomMembersWithoutMe = roomMembers?.filter(item => item.userId !== userInfo?.userId);

    // タイトル
    const [title, setTitle] = useState("");
    const handleChangeTitle = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setTitle(event.target.value);
    }

    // エディター
    const [currentContentAsJSON, setCurrentContentAsJSON] = useState<string>('');
    const [targetEditTopicEditorState, setTargetEditTopicEditorState] = useState<string | undefined>(undefined);

    // 公開範囲
    const [openRange, setOpenRange] = React.useState('open');
    const handleChangeOpenRange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOpenRange(event.target.value);
        setTopicUsers([]);
    };

    // トピックユーザー
    const [topicUsers, setTopicUsers] = useState<number[]>([]);
    const handleChangeTopicUsers = (event: SelectChangeEvent<typeof topicUsers>) => {
        const { target: { value } } = event;
        if (typeof value === 'string') { return false };
        setTopicUsers(value);
    }
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: 48 * 4.5 + 8,
                width: 250,
            },
        },
    };

    // バリデーションメッセージ
    const [titleValidationMsg, setTitleValidationMsg] = useState("");
    const [topicUsersValidationMsg, setTopicUsersValidationMsg] = useState("");
    const [bodyValidationMsg, setBodyValidationMsg] = useState("");
    const setValidationMsg = (errors: ValidationErrorsType) => {
        errors.forEach(error => {
            if (error.key === "Title") {
                setTitleValidationMsg(error.message);
            }
            if (error.key === "TopicUsers") {
                setTopicUsersValidationMsg(error.message);
            }
            if (error.key === "Body") {
                setBodyValidationMsg(error.message);
            }
        })
    }
    const initValidationMsg = () => {
        setTitleValidationMsg("");
        setTopicUsersValidationMsg("");
        setBodyValidationMsg("");
    }

    // 編集の場合：編集トピックの値をセット
    useEffect(() => {
        if (isEdit && isOpen) {
            (async () => {
                const response = await getTopic(targetEditTopic.toString());
                if (response) {
                    // タイトル
                    setTitle(response.title);
                    // 本文
                    setTargetEditTopicEditorState(response.body);
                    // 公開範囲
                    setOpenRange(response.openRange === 1 ? "open" : "close");
                    // トピックユーザー
                    const topicMemberIds = response.topicMembers
                        ? response.topicMembers.map((member: { userId: number, userName: string }) => member.userId)
                        : [];
                    setTopicUsers(topicMemberIds);
                }
            })()
        }
    }, [isEdit, isOpen, targetEditTopic])

    // 投稿、更新時のリクエストボディ生成
    const fnCreateTopicContent = (saveAsDraft: boolean) => {
        return {
            body: currentContentAsJSON,
            openRange: openRange === "open" ? 1 : 0,
            postStatus: saveAsDraft ? 0 : 1,
            title: title,
            topicUsers: topicUsers,
            userId: userId,
        }
    }

    // フォームの初期化
    const fnInitForm = () => {
        setTitle('');
        setOpenRange("open");
        setTopicUsers([]);
        setTargetEditTopicEditorState(undefined);
    }

    /**
     * ボタンアクション
     */
    function handleCancel() {
        fnGetRoomTopics(roomId, userId);
        setIsOpen(false);
        fnInitForm();
        setIsEdit(false);
        handleCloseSnackbar();
        initValidationMsg();
    }

    function saveTopicAsDraft() {
        const saveAsDraft = true;
        handleSaveTopic(saveAsDraft);
    }

    function saveTopicAsPublished() {
        const saveAsDraft = false;
        handleSaveTopic(saveAsDraft);
    }

    const handleSaveTopic = async (saveAsDraft: boolean) => {
        const topicContent = fnCreateTopicContent(saveAsDraft);
        const result = isEdit
            ? await updateTopic(targetEditTopic.toString(), topicContent)
            : await addTopic(roomId, topicContent);
        initValidationMsg();
        if (!result) { return false };
        // 検証エラー
        if ("error" in result && result.error.validationErrors) {
            setValidationMsg(result.error.validationErrors);
        }
        // success
        if (result.status === 200) {
            if (!saveAsDraft) {
                fnGetRoomTopics(roomId, userId);
                setIsOpen(false);
                setIsEdit(false);
                fnInitForm();
            }
            if (saveAsDraft) {
                setIsOpenSnackbar(true);
                setTextOnSnackbar(isEdit ? '非公開にしました。' : '下書きを保存しました。');
            }
            initValidationMsg();
        }
    }

    /**
     * テキスト
     */
    const [modalTitle, setModalTitle] = useState(""); // モーダルタイトル
    const [modalLead, setModalLead] = useState(<></>); // モーダルリード文
    const [draftButtonText, setDraftButtonText] = useState(""); // 下書きボタンのテキスト
    useEffect(() => {
        if (topicType === "new") {
            setModalTitle("新しいトピックを作成");
            setModalLead(<>議論が盛り上がる内容にしましょう！<br />
                「トピック作成ページ」へようこそ。こちらでは、あなたが興味を持っているトピックについて新しくディスカッションを立ち上げることができます。メンバーがさまざまなテーマについて自由に情報交換し、学び合う場としてご活用ください。</>);
            setDraftButtonText("下書き保存");
        } else if (topicType === "published") {
            setModalTitle("トピックの編集");
            setModalLead(<>「トピックの編集ページ」へようこそ。こちらでは、公開済みトピックを編集・アップデートすることができます。</>);
            setDraftButtonText("非公開にする");
        } else if (topicType === "draft") {
            setModalTitle("下書きトピックの編集");
            setModalLead(<>「下書きページ」へようこそ。このページは、トピックを作成・公開する前に、アイデアや草案を手軽に保存するためのスペースです。</>);
            setDraftButtonText("下書き保存");
        }
    }, [topicType]);

    /**
     * 確認ダイアログ
     */
    const [agreeButtonDisplay, setAgreeButtonDisplay] = useState("");
    const [confirmDialogBody, setConfirmDialogBody] = useState<string | JSX.Element>("");
    const [confirmDialogTitle, setConfirmDialogTitle] = useState("");
    const [handleConfirmDialogAgree, setHandleConfirmDialogAgree] = useState<() => void>(() => {});
    const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
    
    const handleConfirmDialogDisagree = () => {
        setAgreeButtonDisplay("");
        setIsOpenConfirmDialog(false);
        setConfirmDialogBody("");
        setConfirmDialogTitle("");
        setHandleConfirmDialogAgree(() => {});
    }

    /**
     * スナックバー
     */
    const [isOpenSnackbar, setIsOpenSnackbar] = useState(false);
    const [textOnSnackbar, setTextOnSnackbar] = useState('');

    const handleCloseSnackbar = () => {
        setIsOpenSnackbar(false);
        setTextOnSnackbar('');
    }

    return (
        <FullScreenModal
            fnOnClose={() => {
                setAgreeButtonDisplay("OK");
                setConfirmDialogBody("キャンセルすると編集途中の内容は反映されません。");
                setConfirmDialogTitle("編集をキャンセルしますか？");
                setHandleConfirmDialogAgree(() => handleCancel);
                setIsOpenConfirmDialog(true);
            }}
            isOpen={isOpen}
        >
            <Box
                sx={{textAlign: "center"}}
            >
                <Typography id="modal-modal-title" variant="h1" component="h1" fontWeight="600" sx={{ fontSize: { mb: 20, tb: 24, pc: 32 } }}>
                    {modalTitle}
                </Typography>
                <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                    {modalLead}
                </Typography>
            </Box>
            <Box>
                {/*タイトル*/}
                <TextField
                    error={!!titleValidationMsg}
                    label="タイトル"
                    variant="outlined"
                    size="small"
                    sx={{ width: "100%", mt: 3 }}
                    onChange={(event) => {
                        event.preventDefault();
                        handleChangeTitle(event);
                    }}
                    value={title}
                    helperText={titleValidationMsg}
                />

                <Typography variant="subtitle1" component="h2" fontWeight="600" sx={{ mt: 3 }}>
                    メンバー招待
                </Typography>

                {/*公開範囲*/}
                <FormControl sx={{ mt: 1, width: "100%" }}>
                    <RadioGroup
                        aria-label="gender"
                        defaultValue="open"
                        name="radio-buttons-group"
                        onChange={handleChangeOpenRange}
                        row
                        sx={{
                            justifyContent: "start",
                        }}
                        value={openRange}
                    >
                        <FormControlLabel value="open" control={<Radio />} label="公開トピック" />
                        <FormControlLabel value="close" control={<Radio />} label="招待制（クローズド）" />
                    </RadioGroup>
                </FormControl>

                {/*メンバー招待*/}
                <FormControl
                    sx={{
                        width: "100%",
                        maxWidth: 400,
                        mt: 1,
                    }}
                    size="small"
                    disabled={openRange === "open"}
                >
                    <InputLabel>ユーザーを招待</InputLabel>
                    <Select
                        error={!!topicUsersValidationMsg}
                        multiple
                        value={topicUsers}
                        onChange={handleChangeTopicUsers}
                        input={<OutlinedInput label="ユーザーを招待" />}
                        MenuProps={MenuProps}
                    >
                        {roomMembersWithoutMe?.map((member) => <MenuItem value={member.userId} key={member.userId}>{member.userName}</MenuItem>)}
                    </Select>
                    {(topicUsersValidationMsg !== "" && openRange === "close") ? (
                        <FormHelperText error={true}>{topicUsersValidationMsg}</FormHelperText>
                    ) : <></>}
                </FormControl>

                {/*本文*/}
                <Box sx={{ mt: 3 }}>
                    <Lexical
                        error={!!bodyValidationMsg}
                        helperText={bodyValidationMsg}
                        setCurrentContentAsJSON={setCurrentContentAsJSON}
                        defaultContentAsJSON={targetEditTopicEditorState}
                        isEditable={true}
                    />
                </Box>

                {/*ボタン*/}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        flexWrap: { mb: "wrap", tb: "nowrap" },
                        mt: 2,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => {
                            if (topicType === "new" || topicType === "draft") {
                                saveTopicAsDraft();
                            } else if (topicType === "published") {
                                setAgreeButtonDisplay("非公開にする");
                                setConfirmDialogBody(<>非公開にすると参加メンバーはこのトピックが見えなくなります。<br />また、非公開にしたトピックは下書き状態にもどります。</>);
                                setConfirmDialogTitle("このトピックを非公開にします。よろしいですか？");
                                setHandleConfirmDialogAgree(() => saveTopicAsDraft);
                                setIsOpenConfirmDialog(true);
                            }
                        }}
                        size="small"
                        sx={{
                            width: {mb: "calc(50% - 8px)", tb: "unset"},
                        }}
                    >
                        {draftButtonText}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            if (topicType === "new") {
                                setAgreeButtonDisplay("OK");
                                setConfirmDialogBody("キャンセルすると作成途中の内容は反映されません。");
                                setConfirmDialogTitle("作成をキャンセルしますか？");
                            } else if (topicType === "published") {
                                setAgreeButtonDisplay("OK");
                                setConfirmDialogBody("キャンセルすると編集途中の内容は反映されません。");
                                setConfirmDialogTitle("編集をキャンセルしますか？");
                            } else if (topicType === "draft") {
                                setAgreeButtonDisplay("削除する");
                                setConfirmDialogBody(<>入力した内容は削除されます。<br />下書き保存されていない内容は削除されます。</>);
                                setConfirmDialogTitle("編集をキャンセルしますか？");
                            }

                            setHandleConfirmDialogAgree(() => handleCancel);
                            setIsOpenConfirmDialog(true);
                        }}
                        size="small"
                        sx={{ width: { mb: "calc(50% - 8px)", tb: "unset" }, ml: 2 }}
                    >
                        キャンセル
                    </Button>
                    <Button
                        onClick={() => {
                            if (topicType === "new") {
                                setAgreeButtonDisplay("公開する");
                                setConfirmDialogBody("トピック公開後も編集することができます。");
                                setConfirmDialogTitle("作成した内容を公開してよろしいですか？");
                            } else if (topicType === "published") {
                                setAgreeButtonDisplay("更新する");
                                setConfirmDialogBody("編集内容を更新すると元の状態には戻せません。");
                                setConfirmDialogTitle("編集した内容を公開してよろしいですか？");
                            } else if (topicType === "draft") {
                                setAgreeButtonDisplay("公開する");
                                setConfirmDialogBody("トピック公開後も編集することができます。");
                                setConfirmDialogTitle("編集した内容を公開してよろしいですか？");
                            }

                            setHandleConfirmDialogAgree(() => saveTopicAsPublished);
                            setIsOpenConfirmDialog(true);
                        }}
                        variant="contained"
                        size="small"
                        sx={{
                            width: { mb: "100%", tb: "unset" },
                            ml: { mb: 0, tb: 2 },
                            mt: { mb: 1.5, tb: 0 },
                        }}
                    >
                        公開する
                    </Button>
                </Box>
            </Box>

            {/*各確認ダイアログ*/}
            <ConfirmDialog
                agreeButtonDisplay={agreeButtonDisplay}
                body={confirmDialogBody}
                handleClickAgree={handleConfirmDialogAgree}
                handleClickDisagree={handleConfirmDialogDisagree}
                isOpen={isOpenConfirmDialog}
                setIsOpen={setIsOpenConfirmDialog}
                title={confirmDialogTitle}
            />

            {/* スナックバー */}
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={isOpenSnackbar}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {textOnSnackbar}
                </Alert>
            </Snackbar>

        </FullScreenModal>
    );
}